// MUST mirror backend/app/i18n/locale.py::Locale enum values + FALLBACK_CHAINS.
// When adding a locale: update BOTH files + add catalog JSONs under ./locales/<code>/.

export type Locale = 'pl' | 'en' | 'cs'

export interface LocaleMeta {
  /** BCP 47 tag for Intl / Babel formatting. */
  bcp47: string
  /** Native language name shown in the language selector dropdown. */
  nativeName: string
  /** Fallback chain for missing keys (excluding self — i18next appends self first). */
  fallback: Locale[]
}

export const LOCALES: Record<Locale, LocaleMeta> = {
  pl: { bcp47: 'pl-PL', nativeName: 'Polski', fallback: [] },
  en: { bcp47: 'en-GB', nativeName: 'English', fallback: ['pl'] },
  cs: { bcp47: 'cs-CZ', nativeName: 'Čeština', fallback: ['en', 'pl'] },
}

export const SUPPORTED_LOCALES = Object.keys(LOCALES) as Locale[]
export const DEFAULT_LOCALE: Locale = 'pl'

/** Detector aliases: browser language → our Locale. */
export const LOCALE_ALIASES: Record<string, Locale> = {
  sk: 'cs',
}

export const isSupportedLocale = (v: unknown): v is Locale =>
  typeof v === 'string' && v in LOCALES

export function toBcp47(lang: string): string {
  const primary = lang.split(/[-_]/)[0]?.toLowerCase() ?? ''
  const resolved = (LOCALE_ALIASES[primary] ?? primary) as Locale
  return LOCALES[resolved]?.bcp47 ?? LOCALES[DEFAULT_LOCALE].bcp47
}

/** i18next `fallbackLng` map derived from LOCALES. */
export const FALLBACK_LNG: Record<string, string[]> & { default: string[] } = {
  default: [DEFAULT_LOCALE],
  ...Object.fromEntries(
    (Object.entries(LOCALES) as [Locale, LocaleMeta][])
      .filter(([, meta]) => meta.fallback.length > 0)
      .map(([code, meta]) => [code, meta.fallback]),
  ),
}

/** Walk the fallback chain for a locale (self first). Used by site-notice picker etc. */
export function fallbackChain(locale: string): Locale[] {
  const primary = locale.split(/[-_]/)[0]?.toLowerCase() ?? ''
  const resolved = (LOCALE_ALIASES[primary] ?? primary) as Locale
  if (!isSupportedLocale(resolved)) return [DEFAULT_LOCALE]
  return [resolved, ...LOCALES[resolved].fallback]
}
